<script setup>
import { computed, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { onKeyStroke } from '@vueuse/core';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import { useGgFollowupSelection } from 'dashboard/composables/useGgFollowup';
import { useGgSupportState } from 'dashboard/composables/useGgSupportState';
import GgFollowupAPI from 'dashboard/api/ggFollowup';
import GgMessageRange from '../gg-followup/GgMessageRange.vue';
import GgFollowupOperation from '../gg-followup/GgFollowupOperation.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  conversationId: { type: Number, required: true },
  ticketId: { type: Number, required: true },
});
const { t, te } = useI18n();
const { selection, close, reset, setLocked } = useGgFollowupSelection();
const request = useAbortableRequest();
const context = ref(null);
const operation = ref(null);
const considerations = ref('');
const closingText = ref('');
const reviewedRequestId = ref(null);
const error = ref('');
const otherActiveTarget = ref(false);
const MAX_TEXT_LENGTH = 120000;
const TERMINAL_STATES = ['saved', 'failed', 'superseded'];
const {
  current,
  operation: stateOperation,
  error: stateError,
  busy: stateBusy,
  pending: statePending,
  blocked: stateBlocked,
  noteFailed,
  intent,
  dialog: stateDialog,
  refresh: refreshState,
  open: openState,
  submit: submitState,
  resume: recoverState,
} = useGgSupportState(
  () => props.conversationId,
  () => props.ticketId,
  true
);
const saved = computed(
  () =>
    stateOperation.value?.state === 'saved' &&
    stateOperation.value.payload.action === 'complete'
);
const stateErrorLabel = computed(() => {
  const code = stateOperation.value?.last_error_code || stateError.value;
  const key = `GG_SUPPORT_STATE.ERRORS.${code}`;
  return code
    ? t(te(key) ? key : 'GG_SUPPORT_STATE.ERRORS.REQUEST_FAILED')
    : '';
});
const stateUnavailable = computed(
  () =>
    stateBusy.value ||
    statePending.value ||
    stateBlocked.value ||
    saved.value ||
    Boolean(intent.value)
);
const canGenerate = computed(
  () =>
    !operation.value &&
    context.value?.support_registration_available &&
    !otherActiveTarget.value &&
    current.value?.allowed_actions.includes('complete') &&
    !stateUnavailable.value &&
    (selection.first || considerations.value.trim()) &&
    !selection.resolving &&
    !selection.rangeError &&
    !selection.choice &&
    (!selection.first || !selection.target) &&
    !request.isPending.value &&
    considerations.value.length <= MAX_TEXT_LENGTH
);
const canComplete = computed(
  () =>
    operation.value?.status === 'ready' &&
    closingText.value.trim().length > 0 &&
    [...closingText.value.trim()].length <= MAX_TEXT_LENGTH &&
    !closingText.value.includes('\0') &&
    !request.isPending.value &&
    !stateUnavailable.value &&
    current.value?.allowed_actions.includes('complete')
);
const showError = exception => {
  const key = `GG_FOLLOWUP.ERRORS.${exception.response?.data?.code}`;
  error.value = t(te(key) ? key : 'GG_FOLLOWUP.ERRORS.REQUEST_FAILED');
};
const load = async (restoreOperation = true) => {
  error.value = '';
  try {
    const response = await request.run(signal =>
      GgFollowupAPI.context(props.conversationId, {}, { signal })
    );
    if (!response) return;
    context.value = response.data;
    if (restoreOperation) {
      const active = response.data.operation;
      const exists = active && !TERMINAL_STATES.includes(active.status);
      otherActiveTarget.value =
        exists &&
        (active.domain !== 'support' ||
          active.ticket_id !== props.ticketId ||
          active.confirmation_pending);
      operation.value = exists && !otherActiveTarget.value ? active : null;
      if (operation.value)
        considerations.value = operation.value.payload.considerations || '';
    }
  } catch (exception) {
    showError(exception);
  }
};
const generate = async () => {
  if (!canGenerate.value) return;
  error.value = '';
  try {
    const preview = await request.run(signal =>
      GgFollowupAPI.preview(
        props.conversationId,
        selection.first?.id,
        selection.last?.id,
        {
          domain: 'support',
          ticket_reference: String(props.ticketId),
          considerations: considerations.value,
        },
        { signal }
      )
    );
    if (!preview) return;
    operation.value = preview.data.operation;
    if (!operation.value) {
      error.value = t('GG_SUPPORT_STATE.GENERATION_UNAVAILABLE');
      return;
    }
    if (operation.value.status !== 'draft') return;
    const response = await request.run(signal =>
      GgFollowupAPI.analyze(props.conversationId, operation.value.request_id, {
        signal,
      })
    );
    if (response) operation.value = response.data.operation;
  } catch (exception) {
    showError(exception);
  }
};
const reanalyze = async () => {
  if (!canComplete.value) return;
  error.value = '';
  try {
    const response = await request.run(signal =>
      GgFollowupAPI.restart(
        props.conversationId,
        operation.value.request_id,
        'reanalyze',
        {
          signal,
          analysisText: closingText.value.trim(),
        }
      )
    );
    if (response) operation.value = response.data.operation;
  } catch (exception) {
    showError(exception);
  }
};
const resetOperation = () => {
  operation.value = null;
  reset();
  load(false);
};
const complete = async () => {
  if (!canComplete.value) return;
  openState('complete');
  await submitState({ closing_text: closingText.value.trim(), reviewed: true });
  if (
    !stateDialog.value &&
    stateOperation.value &&
    stateOperation.value.state !== 'failed'
  ) {
    try {
      await request.run(signal =>
        GgFollowupAPI.restart(
          props.conversationId,
          operation.value.request_id,
          'reselect',
          { signal }
        )
      );
    } catch (exception) {
      showError(exception);
    }
  }
};
watch(operation, value => {
  if (
    value?.status === 'ready' &&
    value.request_id !== reviewedRequestId.value
  ) {
    closingText.value =
      value.result?.suggestion?.text || value.result?.texto_sugerido || '';
    reviewedRequestId.value = value.request_id;
  }
});
watch(
  () => stateDialog.value?.closingText,
  text => {
    if (text) {
      closingText.value = text;
    }
  }
);
watch(
  () =>
    Boolean(operation.value) ||
    request.isPending.value ||
    stateUnavailable.value,
  locked => setLocked(locked),
  { immediate: true, flush: 'sync' }
);
watch(
  () => [props.conversationId, props.ticketId],
  () => {
    request.abort();
    context.value = null;
    operation.value = null;
    considerations.value = '';
    closingText.value = '';
    reviewedRequestId.value = null;
    otherActiveTarget.value = false;
    load();
  },
  { immediate: true }
);
onKeyStroke('Escape', close);
onUnmounted(() => setLocked(false));
</script>

<template>
  <aside
    aria-labelledby="gg-support-closing-title"
    class="fixed bottom-0 inset-x-0 z-40 h-[45dvh] flex flex-col bg-n-surface-1 border-t border-n-weak shadow-lg md:static md:h-full md:w-[22rem] md:min-w-[22rem] md:border-t-0 md:border-s md:shadow-none 2xl:w-[26rem] 2xl:min-w-[26rem]"
  >
    <header
      class="flex items-center justify-between gap-2 px-4 py-3 border-b border-n-weak shrink-0"
    >
      <h2
        id="gg-support-closing-title"
        class="m-0 text-sm font-semibold text-n-slate-12"
      >
        {{ t('GG_SUPPORT_STATE.ACTIONS.complete') }}
      </h2>
      <Button
        :aria-label="t('GG_FOLLOWUP.CLOSE')"
        icon="i-lucide-x"
        color="slate"
        variant="ghost"
        size="sm"
        @click="close"
      />
    </header>
    <div class="flex-1 min-h-0 overflow-y-auto p-4 flex flex-col gap-4">
      <p v-if="current" class="m-0 text-sm text-n-slate-12">
        {{
          t('GG_SUPPORT_STATE.CONFIRM_CONTEXT', {
            code: current.ticket.ticket_code,
            state: current.ticket.state_label,
          })
        }}
      </p>
      <p
        v-if="error || stateErrorLabel"
        role="alert"
        class="m-0 text-sm text-n-ruby-11"
      >
        {{ error || stateErrorLabel }}
      </p>
      <p v-if="statePending" role="status" class="m-0 text-sm text-n-slate-11">
        {{ t('GG_SUPPORT_STATE.WAITING') }}
      </p>
      <p v-if="saved" role="status" class="m-0 text-sm text-n-teal-11">
        {{
          t('GG_SUPPORT_STATE.SAVED', {
            code: stateOperation.result.ticket.ticket_code,
          })
        }}
      </p>
      <Button
        v-if="stateBlocked || noteFailed || intent"
        :label="
          t(
            noteFailed
              ? 'GG_SUPPORT_STATE.RECOVER_NOTE'
              : 'GG_SUPPORT_STATE.RECOVER'
          )
        "
        :disabled="stateBusy"
        @click="recoverState"
      />
      <Button
        v-if="stateErrorLabel && !statePending"
        :label="t('GG_SUPPORT_STATE.REFRESH')"
        :disabled="stateBusy"
        color="slate"
        variant="faded"
        @click="refreshState"
      />
      <Spinner
        v-if="(request.isPending.value && !context) || (stateBusy && !current)"
      />
      <Button
        v-if="error && !context"
        :label="t('GG_FOLLOWUP.RETRY')"
        @click="load()"
      />
      <template v-if="context && !saved">
        <p
          v-if="otherActiveTarget"
          role="status"
          class="m-0 text-sm text-n-amber-11"
        >
          {{ t('GG_SUPPORT_HEADER.ACTIVE_ANALYSIS') }}
        </p>
        <p
          v-else-if="!context.support_registration_available"
          role="alert"
          class="m-0 text-sm text-n-amber-11"
        >
          {{ t('GG_SUPPORT_STATE.GENERATION_UNAVAILABLE') }}
        </p>
        <GgFollowupOperation
          v-else-if="operation"
          :key="operation.request_id"
          :conversation-id="conversationId"
          :operation="operation"
          use-text-only
          :disabled="request.isPending.value || stateUnavailable"
          @update="operation = $event"
          @reset="resetOperation"
          @reselect="resetOperation"
          @reload="load()"
        >
          <template #review>
            <TextArea
              id="gg-support-closing-text"
              v-model="closingText"
              :max-length="MAX_TEXT_LENGTH"
              show-character-count
              :label="t('GG_SUPPORT_STATE.CLOSING_TEXT')"
              custom-text-area-class="!h-64"
              :disabled="stateUnavailable || request.isPending.value"
            />
            <p class="m-0 text-sm text-n-slate-11">
              {{ t('GG_SUPPORT_STATE.DIRECT_REVIEW_HINT') }}
            </p>
          </template>
        </GgFollowupOperation>
        <template v-else-if="!otherActiveTarget">
          <p class="m-0 text-sm text-n-slate-11">
            {{ t('GG_SUPPORT_STATE.SELECT_CLOSING_MESSAGES') }}
          </p>
          <GgMessageRange
            :conversation-id="conversationId"
            :disabled="request.isPending.value || stateUnavailable"
          />
          <TextArea
            id="gg-support-closing-considerations"
            v-model="considerations"
            :max-length="MAX_TEXT_LENGTH"
            show-character-count
            :label="t('GG_SUPPORT_STATE.CONSIDERATIONS')"
            :placeholder="t('GG_SUPPORT_STATE.CONSIDERATIONS_PLACEHOLDER')"
            custom-text-area-class="!h-32"
            :disabled="request.isPending.value || stateUnavailable"
          />
        </template>
      </template>
    </div>
    <footer class="flex flex-col gap-2 p-4 border-t border-n-weak shrink-0">
      <Button
        v-if="operation?.status === 'ready' && !saved"
        :label="t('GG_SUPPORT_STATE.COMPLETE_DIRECT')"
        :disabled="!canComplete"
        :is-loading="stateBusy"
        @click="complete"
      />
      <Button
        v-if="operation?.status === 'ready' && !saved"
        :label="t('GG_SUPPORT_STATE.REANALYZE_EDITED')"
        color="slate"
        variant="faded"
        :disabled="!canComplete"
        :is-loading="request.isPending.value"
        @click="reanalyze"
      />
      <Button
        v-else-if="!operation && !saved"
        icon="i-lucide-sparkles"
        :label="t('GG_SUPPORT_STATE.SEND_CLOSING_ANALYSIS')"
        :disabled="!canGenerate"
        :is-loading="request.isPending.value"
        @click="generate"
      />
      <Button
        :label="t(saved ? 'GG_FOLLOWUP.CLOSE' : 'DIALOG.BUTTONS.CANCEL')"
        color="slate"
        variant="faded"
        @click="close"
      />
    </footer>
  </aside>
</template>
